import { localStorage, sessionStorage } from 'dibs-browser-storage';

import {
    APP_DOWNLOAD_LOCAL_STORAGE_KEYS,
    APP_DOWNLOAD_SESSION_STORAGE_KEYS,
    APP_DOWNLOAD_MODAL_TYPES,
} from './constants.ts';
import {
    type AppDownloadModalType,
    type AppDownloadModalData,
    type AppDownloadStorage,
} from './types.ts';

const { appDownloadModalPrompt } = APP_DOWNLOAD_MODAL_TYPES;

const isJsonObject = (storageObj: JsonType): storageObj is JsonObject =>
    !!storageObj && typeof storageObj === 'object' && !Array.isArray(storageObj);

function safeNumber(value: unknown): number {
    return typeof value === 'number' && !isNaN(value) ? value : 0;
}

function sanitizeAppDownloadModalData(data: AppDownloadModalData): AppDownloadModalData {
    return { timestamp: safeNumber(data.timestamp), count: safeNumber(data.count) };
}

function getAppDownloadStorage(): Partial<AppDownloadStorage> {
    const storageObj = localStorage.getItem(APP_DOWNLOAD_LOCAL_STORAGE_KEYS.APP_DOWNLOAD_DATA);

    return isJsonObject(storageObj) ? storageObj : {};
}

function getAppDownloadModalData(type: AppDownloadModalType): AppDownloadModalData | undefined {
    const storage = getAppDownloadStorage();

    if (storage[type]) {
        return sanitizeAppDownloadModalData(storage[type]);
    }

    // Backwards compatibility for legacy 'appDownloadModalPrompt'
    if (type === appDownloadModalPrompt) {
        const legacy = localStorage.getItem(
            APP_DOWNLOAD_LOCAL_STORAGE_KEYS.LEGACY_APP_DOWNLOAD_DATA
        );

        if (legacy && isJsonObject(legacy)) {
            storage[type] = legacy as AppDownloadModalData;

            localStorage.setItem(APP_DOWNLOAD_LOCAL_STORAGE_KEYS.APP_DOWNLOAD_DATA, storage);
            localStorage.deleteItem(APP_DOWNLOAD_LOCAL_STORAGE_KEYS.LEGACY_APP_DOWNLOAD_DATA);

            return sanitizeAppDownloadModalData(storage[type]);
        }
    }

    return undefined;
}

function getAppDownloadModalCount(type: AppDownloadModalType): number {
    const appDownloadModalData = getAppDownloadModalData(type);

    return appDownloadModalData?.count || 0;
}

function setAppDownloadOnCooldown(type: AppDownloadModalType): void {
    const appDownloadStorage = getAppDownloadStorage();
    const existing = getAppDownloadModalData(type);

    appDownloadStorage[type] = {
        timestamp: Date.now(),
        count: (existing?.count ?? 0) + 1,
    };

    localStorage.setItem(APP_DOWNLOAD_LOCAL_STORAGE_KEYS.APP_DOWNLOAD_DATA, appDownloadStorage);
    sessionStorage.setItem(APP_DOWNLOAD_SESSION_STORAGE_KEYS.SEEN, 'true');
}

function isAppDownloadOnCooldown(type: AppDownloadModalType): boolean {
    if (sessionStorage.getItem(APP_DOWNLOAD_SESSION_STORAGE_KEYS.SEEN)) {
        return true;
    }

    const data = getAppDownloadModalData(type);

    if (!data) {
        return false;
    }

    const timeSinceDismissed = Date.now() - data.timestamp;
    const hour = 60 * 60 * 1000;
    const day = 24 * hour;

    switch (data.count) {
        case 0:
            return false;
        case 1:
            return timeSinceDismissed < 2 * hour;
        case 2:
            return timeSinceDismissed < day;
        case 3:
            return timeSinceDismissed < 7 * day;
        default:
            return timeSinceDismissed < 28 * day;
    }
}

export { isAppDownloadOnCooldown, setAppDownloadOnCooldown, getAppDownloadModalCount };
